import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Table, TableHeader, TableBody, Pagination, Search, useClientTable } from '../../../components/table';
import { Dialog } from '../../../components/ui';
import NoData from '../../../components/common/NoData';
import Loader from '../../../components/common/Loader';
import { getTraineeAnalytics } from '../../../services/organization/trainees/trainerAnalytics.service';
import { getSignedMediaUrl } from '../../../services/media.service';
import { toast } from '../../../components/ui/notify';
import { useTimezone } from '../../../context/TimezoneContext';
import { formatUtcInTimeZone } from '../../../utils/timezone';

const sortCertificatesByIssuedDate = (certificates = []) => (
  [...certificates].sort((a, b) => {
    const bTime = new Date(b.issued_date || b.createdAt || b.created_at || 0).getTime() || 0;
    const aTime = new Date(a.issued_date || a.createdAt || a.created_at || 0).getTime() || 0;
    return bTime - aTime;
  })
);

const hasCertificateFile = (certificate) => {
  const value = String(certificate.certificate_url || '').trim();
  return Boolean(value && value !== 'temp_placeholder');
};

function TraineeAnalyticsCertificatesTab({ traineeId }) {
  const { timeZone } = useTimezone();
  const [loading, setLoading] = useState(false);
  const [certificatesData, setCertificatesData] = useState([]);
  const [preview, setPreview] = useState(null);
  const previewRequestRef = useRef(0);
  const table = useClientTable(certificatesData, (certificate) => `${certificate.course_name || ''} ${certificate.certificate_name || ''}`);
  const availableFiles = useMemo(() => certificatesData.filter(hasCertificateFile).length, [certificatesData]);

  useEffect(() => {
    if (!traineeId) {
      setCertificatesData([]);
      return;
    }

    let active = true;
    const fetchCertificatesData = async () => {
      try {
        setLoading(true);
        const response = await getTraineeAnalytics({ trainee_id: traineeId });
        if (!active) return;

        if (response.success) {
          setCertificatesData(sortCertificatesByIssuedDate(response.data?.certificates || []));
        } else {
          setCertificatesData([]);
          toast.error(response.message || 'Failed to fetch certificates data');
        }
      } catch (error) {
        if (!active) return;
        setCertificatesData([]);
        console.error('Error fetching certificates data:', error);
        toast.error('Something went wrong while fetching certificates data');
      } finally {
        if (active) setLoading(false);
      }
    };

    fetchCertificatesData();
    return () => { active = false; };
  }, [traineeId]);

  const formatDate = (dateString) => dateString
    ? formatUtcInTimeZone(dateString, timeZone, 'MMM D, YYYY')
    : '—';

  const openCertificate = async (certificate) => {
    const requestId = ++previewRequestRef.current;
    setPreview({ certificate, url: null, loading: true });
    try {
      const url = await getSignedMediaUrl({ type: 'certificate', mediaId: certificate.id, kind: 'web' });
      if (previewRequestRef.current !== requestId) return;
      setPreview({ certificate, url, loading: false });
    } catch (error) {
      if (previewRequestRef.current === requestId) {
        setPreview({ certificate, url: null, loading: false });
      }
    }
  };

  const closePreview = () => {
    previewRequestRef.current += 1;
    setPreview(null);
  };

  const isImagePreview = /\.(png|jpe?g|webp|gif)(?:[?#]|$)/i.test(preview?.certificate?.certificate_url || '');

  if (loading) {
    return (
      <div className="tw-flex tw-justify-center tw-items-center" style={{ minHeight: '200px' }}>
        <Loader />
      </div>
    );
  }

  return (
    <div className="ui-card tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white tw-shadow-sm">
      <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3 tw-p-4">
        <Search value={table.query} onChange={(event) => table.setQuery(event.target.value)} placeholder="Search courses or certificates..." className="tw-w-full sm:tw-max-w-sm" />
        <span className="tw-text-sm tw-text-slate-500">{certificatesData.length} certificates · {availableFiles} files available</span>
      </div>
      <div className="tw-w-full tw-overflow-x-auto tw-rounded-lg tw-border-y tw-border-slate-200" role="region" aria-label="Trainee certificates" tabIndex={0}>
        <Table className="ui-table tw-w-full tw-min-w-[680px] tw-border-collapse tw-text-left tw-text-sm">
          <TableHeader>
            <tr className="tw-bg-slate-50 tw-text-slate-600">
              <th className="tw-w-16 tw-px-4 tw-py-3 tw-font-medium">No.</th>
              <th className="tw-min-w-[180px] tw-px-4 tw-py-3 tw-font-medium">Course</th>
              <th className="tw-min-w-[180px] tw-px-4 tw-py-3 tw-font-medium">Certificate</th>
              <th className="tw-whitespace-nowrap tw-px-4 tw-py-3 tw-font-medium">Issued</th>
              <th className="tw-whitespace-nowrap tw-px-4 tw-py-3 tw-font-medium">Expires</th>
              <th className="tw-w-24 tw-px-4 tw-py-3 tw-font-medium">File</th>
            </tr>
          </TableHeader>
          <TableBody>
            {table.visibleRecords.map((certificate, index) => (
              <tr key={certificate.id} className="tw-border-t tw-border-slate-100 hover:tw-bg-slate-50">
                <td className="tw-px-4 tw-py-4">{(table.page - 1) * table.pageSize + index + 1}</td>
                <td className="tw-px-4 tw-py-4 tw-font-medium tw-break-words">{certificate.course_name || 'Course unavailable'}</td>
                <td className="tw-px-4 tw-py-4 tw-break-words">{certificate.certificate_name || 'Certificate of completion'}</td>
                <td className="tw-whitespace-nowrap tw-px-4 tw-py-4">{formatDate(certificate.issued_date)}</td>
                <td className="tw-whitespace-nowrap tw-px-4 tw-py-4">{formatDate(certificate.expiration_date)}</td>
                <td className="tw-px-4 tw-py-4">
                  {hasCertificateFile(certificate) ? (
                    <button
                      type="button"
                      className="tw-rounded-md tw-border tw-border-indigo-200 tw-bg-indigo-50 tw-px-3 tw-py-1.5 tw-font-medium tw-text-indigo-700 hover:tw-bg-indigo-100 disabled:tw-opacity-50"
                      onClick={() => openCertificate(certificate)}
                      aria-label={`View certificate for ${certificate.course_name || 'course'}`}
                    >
                      View
                    </button>
                  ) : <span className="tw-text-slate-400">Unavailable</span>}
                </td>
              </tr>
            ))}
            {table.totalRecords === 0 && (
              <tr><td colSpan="6"><NoData message={certificatesData.length ? 'No matching certificates found' : 'No certificates found'} /></td></tr>
            )}
          </TableBody>
        </Table>
      </div>
      <Pagination {...table} onPageChange={table.setPage} onPageSizeChange={table.setPageSize} className="tw-p-4" />
      <Dialog show={Boolean(preview)} onHide={closePreview} size="xl" centered scrollable>
        <Dialog.Header closeButton>
          <div className="tw-min-w-0">
            <Dialog.Title>{preview?.certificate?.certificate_name || 'Certificate preview'}</Dialog.Title>
            <div className="tw-text-sm tw-text-slate-500">{preview?.certificate?.course_name}</div>
          </div>
        </Dialog.Header>
        <Dialog.Body className="tw-p-3">
          <div className="tw-flex tw-h-[65vh] tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-lg tw-bg-slate-50">
            {preview?.loading ? (
              <Loader />
            ) : preview?.url ? (
              isImagePreview ? (
                <img src={preview.url} alt={preview.certificate.certificate_name || 'Certificate'} className="tw-max-h-full tw-max-w-full tw-object-contain" />
              ) : (
                <iframe src={preview.url} title={preview.certificate.certificate_name || 'Certificate preview'} className="tw-h-full tw-w-full tw-border-0" />
              )
            ) : (
              <p className="tw-text-sm tw-text-slate-600">Certificate file is not available for preview.</p>
            )}
          </div>
        </Dialog.Body>
      </Dialog>
    </div>
  );
}

export default TraineeAnalyticsCertificatesTab;
